import { useEffect, useState } from 'react'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { useLocation, useNavigate } from 'react-router-dom'
import { gettingZones } from 'utils/Zones'
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ReplayIcon from '@mui/icons-material/Replay'
import { Add } from 'components/dialog/zones/Add'
import { Edit } from 'components/dialog/zones/Edit'
import Delete from 'components/dialog/zones/Delete'
import { ZoneProvider } from 'components/dialog/zones/ZoneContext'
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting'

const ContextMenu = ({ contextMenu, setContextMenu, selectedRow, setSelectedRow, reloading, rows }) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()

    const [openDelete, setOpenDelete] = useState(false)
    const basePath = buildSettingsBasePath('zones')
    const modalRoute = parseSettingsCrudRoute(location.pathname, 'zones')
    const openAdd = modalRoute.action === 'add'
    const openEdit = (modalRoute.action === 'update' || modalRoute.action === 'detail') && modalRoute.id !== null
    const selectedRowFromRoute = modalRoute.id === null ? null : rows.find((row) => Number(row.id) === modalRoute.id)
    const selectedRowsForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow

    const closeRouteModal = () => {
        navigate(basePath)
    }

    const options = [
        {
            name: t('settings.zones.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('zones', 'add'))
                setContextMenu(null)
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            permission: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.CreateZone') ? true : false,
        },
        {
            name: t('settings.zones.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0]
                if (!current?.id) return
                navigate(buildSettingsCrudPath('zones', 'update', current.id))
                setContextMenu(null)
            },
            color: '',
            divider: false,
            disabled: selectedRow.length !== 1 ? true : false,
            selected: false,
            onlyOne: true,
            permission: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.UpdateZone') ? true : false,
        },
        {
            name: t('settings.zones.table.context_menu.delete'),
            icon: <DeleteIcon color='error' />,
            click: () => {
                setOpenDelete(true)
                setContextMenu(null)
            },
            color: 'error',
            divider: false,
            disabled: true,//selectedRow.length <= 0 ? true : false,
            selected: true,
            onlyOne: false,
            permission: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.DeleteZone') ? true : false,
        },
        {
            name: t('settings.zones.table.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading()
                setContextMenu(null)
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            permission: true
        }
    ]

    return (
        <>
            <Menu
                open={contextMenu ? true : false}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options.filter(option => option.permission).map((option, index) =>
                    <div key={index}>
                        {option.divider ? <Divider /> : null}

                        <MenuItem
                            onClick={option.click}
                            disabled={option.disabled}
                        >
                            <ListItemIcon>
                                {option.icon}
                            </ListItemIcon>

                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />

                            {option.selected ?
                                <Typography sx={{ pl: 3 }} variant="body2" color={option.color}>
                                    {t('settings.paycodes.table.context_menu.selected')}
                                    :
                                    {selectedRow.length}
                                </Typography>
                                :
                                null
                            }

                            {option.onlyOne ?
                                <Typography sx={{ pl: 3 }} variant="body2" color="text.secondary">
                                    {t('settings.paycodes.table.context_menu.only_one')}
                                </Typography>
                                :
                                null
                            }
                        </MenuItem>
                    </div>
                )}
            </Menu>
            <ZoneProvider>
                <Add open={openAdd} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} reloading={reloading} rows={rows} />
                <Edit open={openEdit} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} selectedRow={selectedRowsForRoute} setSelectedRow={setSelectedRow} reloading={reloading} rows={rows} />
                <Delete open={openDelete} setOpen={setOpenDelete} selectedRow={selectedRow} reloading={reloading} />
            </ZoneProvider>
        </>
    )
}


const Table = () => {
    const { t } = useTranslation()

    const columns = [
        { field: 'name', headerName: t('settings.zones.table.columns.name'), flex: 1 },
        { field: 'description', headerName: t('settings.zones.table.columns.description'), flex: 1 },
        {
            field: 'type',
            headerName: t('settings.zones.table.columns.type'),
            flex: 1,
            renderCell: (params) => {
                return <>{t(`settings.zones.fields.type.${params.value}`)}</>
            }
        },
        {
            field: 'requires_approval',
            headerName: t('settings.zones.table.columns.requires_approval'),
            flex: 1,
            align: 'center',
            renderCell: (params) => {
                if (params.value) {
                    return <Typography sx={{ m: 2 }} variant="subtitle2">{t('general.buttons.yes')}</Typography>
                } else {
                    return <Typography sx={{ m: 2 }} variant="subtitle2">{t('general.buttons.no')}</Typography>
                }
            }
        }
    ]

    // Loadings
    const [loadingGettingZones, setLoadingGettingZones] = useState(true)

    // Get And Set
    const [rows, setRows] = useState([])
    const [contextMenu, setContextMenu] = useState(null)
    const [selectedRow, setSelectedRow] = useState([])
    const [selected, setSelected] = useState([])

    const reloading = () => {
        if (JSON.parse(localStorage.getItem('permissions')).find(permision => permision === 'timekeeper.Zones')) {
            gettingZones(setLoadingGettingZones, setRows)
        } else {
            setRows([])
            setLoadingGettingZones(false)
        }
    }

    useEffect(() => {
        reloading()
    }, [])

    return (
        <div style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                    // ✅ SOLUCIÓN: Forzar blur de elementos con focus antes de abrir menú
                if (document.activeElement && document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{
                    height: 'calc(100vh - 140px)',
                    cursor: 'default',
                    '& .MuiDataGrid-cell': { cursor: 'pointer' },
                }}
                columns={columns}
                rows={rows}
                showCellVerticalBorder
                showColumnVerticalBorder
                headerFilters
                localeText={
                    {
                        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                        footerRowSelected: (count) =>
                            count > 1
                                ? t('general.table.footerFilters.footerRowSelected.>1', { count: count.toLocaleString() })
                                : t('general.table.footerFilters.footerRowSelected.=1', { count: count.toLocaleString() }),

                        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                        MuiTablePagination: {
                            labelDisplayedRows: ({ from, to, count }) =>
                                t('general.table.footerFilters.labelDisplayedRows', { from: from.toLocaleString(), to: to.toLocaleString(), count: count.toLocaleString() }),
                            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                        },
                    }
                }
                disableColumnMenu
                loading={loadingGettingZones}
                checkboxSelection
                disableRowSelectionOnClick={false}
                keepNonExistentRowsSelected={false}
                rowSelectionModel={selectedRow}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    let row = rows.filter((row) => newRowSelectionModel.includes(row.id))
                    setSelectedRow(newRowSelectionModel);
                    setSelected(row)
                }}
                pagination
                slotProps={{
                    cell: {
                        tabIndex: -1,
                    },
                    row: {
                        tabIndex: -1,
                    }
                }}
            />
            <ContextMenu
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                selectedRow={selected}
                setSelectedRow={setSelectedRow}
                reloading={reloading}
                rows={rows}
            />
        </div>

    )
}

export { Table }